import type { BadgeStyle, Product, ResultChip, SearchQuery, SearchResult } from "./types.js";

// Product → accession badge styling (matches the brand accents
// already used elsewhere on this page for GEOx/QTLx/GWASx/LITx).
const BADGE: Record<Product, BadgeStyle> = {
  geox: { bg: "rgba(217,154,31,.14)", color: "#8A6212", border: "rgba(217,154,31,.45)" },
  qtlx: { bg: "rgba(122,140,78,.14)", color: "#5C6B3A", border: "rgba(122,140,78,.45)" },
  gwasx: { bg: "rgba(30,107,92,.14)", color: "#155248", border: "rgba(30,107,92,.45)" },
  litx: { bg: "rgba(190,83,28,.14)", color: "#8E3D14", border: "rgba(190,83,28,.45)" },
};

const QUERIES: SearchQuery[] = [
  {
    text: "Parkinson's disease",
    summary: "4 results across 4 indexes · 0.28s",
    results: [
      {
        product: "geox",
        accession: "GSE114517",
        title: "PD case/control transcriptomes from substantia nigra",
        date: "May 2018",
        chips: [
          { label: "Disease", value: "Parkinson's", primary: true },
          { label: "Tissue", value: "Substantia nigra", primary: true },
          { label: "Platform", value: "Illumina HiSeq" },
        ],
      },
      {
        product: "qtlx",
        accession: "PMID 32341526",
        title: "Brain-region eQTL and sQTL maps in PD-relevant tissue",
        date: "2020",
        chips: [
          { label: "QTL", value: "eQTL, sQTL", primary: true },
          { label: "Tissue", value: "Substantia nigra", primary: true },
          { label: "Author", value: "Guelfi" },
        ],
      },
      {
        product: "gwasx",
        accession: "GCST009325",
        title: "Identification of novel risk loci for Parkinson disease",
        date: "2019",
        chips: [
          { label: "Category", value: "Disease", primary: true },
          { label: "Ancestry", value: "European" },
          { label: "N", value: "1,474,097", primary: true },
          { label: "Author", value: "Nalls" },
        ],
      },
      {
        product: "litx",
        accession: "PMID 40198432",
        title: "α-synuclein aggregation dynamics in dopaminergic neurons",
        date: "Apr 2025",
        chips: [
          { label: "Score", value: "3", primary: true },
          { label: "Journal", value: "Nat. Neurosci." },
          { label: "Config", value: "neurodegen" },
        ],
      },
    ],
  },
  {
    text: "SNCA gene expression",
    summary: "4 results across 4 indexes · 0.22s",
    results: [
      {
        product: "geox",
        accession: "GSE26927",
        title: "Brain expression across neurodegenerative diseases",
        date: "Aug 2011",
        chips: [
          { label: "Disease", value: "Neurodegenerative", primary: true },
          { label: "Tissue", value: "Brain", primary: true },
          { label: "Platform", value: "Illumina HumanRef-8" },
        ],
      },
      {
        product: "qtlx",
        accession: "PMID 29632383",
        title: "Brain-region eQTL and sQTL analyses across 13 regions",
        date: "2018",
        chips: [
          { label: "QTL", value: "eQTL, sQTL", primary: true },
          { label: "Tissue", value: "Brain (13 regions)", primary: true },
          { label: "Author", value: "Sieberts" },
        ],
      },
      {
        product: "gwasx",
        accession: "GCST003043",
        title: "Trans-ancestry meta-analysis identifies SNCA-region risk",
        date: "2017",
        chips: [
          { label: "Category", value: "Disease", primary: true },
          { label: "Ancestry", value: "Multi-ancestry" },
          { label: "N", value: "448,841", primary: true },
          { label: "Author", value: "Foo" },
        ],
      },
      {
        product: "litx",
        accession: "PMID 39871220",
        title: "Cell-type-specific SNCA regulation in midbrain DA neurons",
        date: "Feb 2025",
        chips: [
          { label: "Score", value: "3", primary: true },
          { label: "Journal", value: "Cell" },
          { label: "Config", value: "neurodegen" },
        ],
      },
    ],
  },
  {
    text: "Alzheimer's disease",
    summary: "4 results across 4 indexes · 0.31s",
    results: [
      {
        product: "geox",
        accession: "GSE48350",
        title: "Hippocampus expression across the AD spectrum and aging",
        date: "Jul 2014",
        chips: [
          { label: "Disease", value: "Alzheimer's", primary: true },
          { label: "Tissue", value: "Hippocampus", primary: true },
          { label: "Platform", value: "Affymetrix Hu Gene 1.0" },
        ],
      },
      {
        product: "qtlx",
        accession: "PMID 33589692",
        title: "CSF pQTLs identify protein regulators of dementia risk",
        date: "2021",
        chips: [
          { label: "QTL", value: "pQTL", primary: true },
          { label: "Tissue", value: "CSF", primary: true },
          { label: "Author", value: "Yang" },
        ],
      },
      {
        product: "gwasx",
        accession: "GCST90027158",
        title: "New insights into AD genetic etiology and related dementias",
        date: "2022",
        chips: [
          { label: "Category", value: "Disease", primary: true },
          { label: "Ancestry", value: "European" },
          { label: "N", value: "487,511", primary: true },
          { label: "Author", value: "Bellenguez" },
        ],
      },
      {
        product: "litx",
        accession: "PMID 40012345",
        title: "Tau-network plasma biomarkers for early AD detection",
        date: "Mar 2025",
        chips: [
          { label: "Score", value: "2", primary: true },
          { label: "Journal", value: "Brain" },
          { label: "Config", value: "neurodegen" },
        ],
      },
    ],
  },
];

function renderChip(chip: ResultChip): HTMLElement {
  const wrap = document.createElement("span");
  wrap.className = "inline-flex items-center px-2 py-0.5 rounded";
  wrap.style.background = chip.primary ? "#EFF6FF" : "#F3F4F6";
  const lbl = document.createElement("span");
  lbl.className = "text-[10px] uppercase tracking-wider text-gray-500 mr-1";
  lbl.textContent = chip.label;
  const val = document.createElement("span");
  val.className = "text-xs font-medium";
  val.style.color = "#111827";
  val.textContent = chip.value;
  wrap.appendChild(lbl);
  wrap.appendChild(val);
  return wrap;
}

function renderResultRow(result: SearchResult): HTMLElement {
  const badge = BADGE[result.product] ?? BADGE.geox;

  const row = document.createElement("div");
  row.className = "dls-row py-3 border-b border-gray-100 last:border-b-0";

  const header = document.createElement("div");
  header.className = "flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2";

  const accession = document.createElement("span");
  accession.className =
    "inline-flex items-center self-start px-2 py-0.5 rounded text-xs font-mono flex-shrink-0";
  accession.style.background = badge.bg;
  accession.style.color = badge.color;
  accession.style.border = `1px solid ${badge.border}`;
  accession.textContent = result.accession;

  const title = document.createElement("span");
  title.className = "text-sm font-semibold leading-snug";
  title.style.color = "#111827";
  title.textContent = result.title;

  const date = document.createElement("span");
  date.className = "text-xs sm:ml-auto flex-shrink-0";
  date.style.color = "#6B7280";
  date.textContent = result.date;

  header.appendChild(accession);
  header.appendChild(title);
  header.appendChild(date);
  row.appendChild(header);

  if (result.chips && result.chips.length) {
    const chipRow = document.createElement("div");
    chipRow.className = "flex items-center gap-2 flex-wrap mt-2";
    result.chips.forEach((c) => chipRow.appendChild(renderChip(c)));
    row.appendChild(chipRow);
  }

  return row;
}

export function initDataLayerSearchDemo(): void {
  const root = document.getElementById("data-layer-search-demo");
  if (!root) return;

  const queryEl = root.querySelector<HTMLElement>("[data-dls-query]");
  const metaEl = root.querySelector<HTMLElement>("[data-dls-meta]");
  const listEl = root.querySelector<HTMLElement>("[data-dls-results]");
  if (!queryEl || !metaEl || !listEl) return;

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const renderResultsStatic = (query: SearchQuery) => {
    listEl.innerHTML = "";
    query.results.forEach((r) => {
      const row = renderResultRow(r);
      row.classList.add("is-in");
      listEl.appendChild(row);
    });
    metaEl.innerHTML = "";
    const dot = document.createElement("span");
    dot.className = "inline-block w-1.5 h-1.5 rounded-full mr-1.5";
    dot.style.background = "#1D9E75";
    dot.style.verticalAlign = "1px";
    metaEl.appendChild(dot);
    metaEl.appendChild(document.createTextNode(query.summary));
  };

  // Static fallback for reduced motion: first query, no cycling.
  if (reduceMotion) {
    const first = QUERIES[0];
    if (!first) return;
    queryEl.textContent = first.text;
    renderResultsStatic(first);
    return;
  }

  const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

  const typeText = async (text: string) => {
    queryEl.textContent = "";
    for (let i = 0; i < text.length; i++) {
      queryEl.textContent += text[i];
      // Small jitter so it doesn't feel mechanical
      await sleep(28 + Math.random() * 30);
    }
  };

  const backspaceText = async () => {
    let t = queryEl.textContent ?? "";
    while (t.length) {
      t = t.slice(0, -1);
      queryEl.textContent = t;
      await sleep(18 + Math.random() * 14);
    }
  };

  const showSearchingMeta = () => {
    metaEl.innerHTML = "";
    const dot = document.createElement("span");
    dot.className = "dls-search-dot";
    dot.setAttribute("aria-hidden", "true");
    metaEl.appendChild(dot);
    metaEl.appendChild(document.createTextNode("Searching across GEOx, QTLx, GWASx, LITx…"));
  };

  const showSummaryMeta = (summary: string) => {
    metaEl.innerHTML = "";
    const dot = document.createElement("span");
    dot.className = "inline-block w-1.5 h-1.5 rounded-full mr-1.5";
    dot.style.background = "#1D9E75";
    dot.style.verticalAlign = "1px";
    metaEl.appendChild(dot);
    metaEl.appendChild(document.createTextNode(summary));
  };

  const revealResults = async (query: SearchQuery) => {
    listEl.innerHTML = "";
    const rows = query.results.map((r) => {
      const el = renderResultRow(r);
      listEl.appendChild(el);
      return el;
    });
    // Force layout then stagger reveal
    void listEl.offsetHeight;
    for (const row of rows) {
      row.classList.add("is-in");
      await sleep(110);
    }
  };

  const fadeOutResults = async () => {
    const rows = Array.from(listEl.querySelectorAll(".dls-row"));
    rows.forEach((r) => r.classList.add("is-out"));
    await sleep(240);
  };

  let cancelled = false;
  const runCycle = async () => {
    let idx = 0;
    while (!cancelled) {
      const q = QUERIES[idx];
      if (!q) return;

      await typeText(q.text);
      showSearchingMeta();
      if (cancelled) return;
      await sleep(480);
      if (cancelled) return;
      await revealResults(q);
      if (cancelled) return;
      showSummaryMeta(q.summary);
      await sleep(4500);
      if (cancelled) return;
      await fadeOutResults();
      if (cancelled) return;
      await backspaceText();

      idx = (idx + 1) % QUERIES.length;
    }
  };

  // Only run while visible — keeps the section from animating off-screen
  // and prevents work when the user isn't looking.
  let running = false;
  const start = () => {
    if (running) return;
    running = true;
    cancelled = false;
    void runCycle();
  };
  const stop = () => {
    cancelled = true;
    running = false;
  };

  if ("IntersectionObserver" in window) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) start();
          else stop();
        });
      },
      { threshold: 0.25 },
    );
    io.observe(root);
  } else {
    start();
  }
}
